/**
 * FRACTURE mode, the minigame (spec §5.4, plan §2.9 `mining.json fracture`). A stability window on the 0..1
 * power bar drifts sinusoidally; its width and peak speed come from the rock's resistance. Holding the laser
 * power inside it fills progress in `fractureSeconds`; above it instability grows with the overshoot until the
 * rock bursts; below it progress decays. A laser whose `maxTier` is below the rock's tier makes no progress.
 *
 * `FractureState` is also a `HeatState`: the runtime steps the laser heat on the same struct with `stepHeat`,
 * and while the laser is in its forced cooldown `stepFracture` treats the beam as off (progress decays).
 * One state per ship laser; `resetFracture` on a new rock keeps the heat. Allocation-free (D-014).
 */
import type { MiningConfig } from '../data/m2Schemas'
import type { FractureReadout } from '../space/contracts'
import type { HeatState } from './heat'

export const FRACTURE_PHASE = {
  idle: 0,
  active: 1,
  success: 2,
  burst: 3,
} as const
export type FracturePhase = (typeof FRACTURE_PHASE)[keyof typeof FRACTURE_PHASE]

/** `stepFracture` results (the runtime emits `fractureStart`, `fractureSuccess`, `fractureBurst`). */
export const FRACTURE_EVENT = {
  none: 0,
  start: 1,
  success: 2,
  burst: 3,
} as const
export type FractureEvent = (typeof FRACTURE_EVENT)[keyof typeof FRACTURE_EVENT]

export interface FractureState extends HeatState {
  /** 0..1; 1 = success. */
  progress: number
  /** 0..1; 1 = burst. */
  instability: number
  phase: FracturePhase
  /** Radians; the window centre is `mid + amp × sin(windowPhase)`. */
  windowPhase: number
}

export function createFractureState(): FractureState {
  return {
    progress: 0,
    instability: 0,
    heat: 0,
    cooldown: 0,
    phase: FRACTURE_PHASE.idle,
    windowPhase: 0,
  }
}

/** Disengage: back to idle with no progress or instability; heat and cooldown stay (they belong to the laser). */
export function resetFracture(state: FractureState, windowPhase: number): void {
  state.progress = 0
  state.instability = 0
  state.phase = FRACTURE_PHASE.idle
  state.windowPhase = windowPhase
}

/** The stability window at a moment (caller-owned). */
export interface FractureWindow {
  center: number
  width: number
  /** d(center)/dt, power-bar fractions per second. */
  speed: number
}

export function createFractureWindow(): FractureWindow {
  return { center: 0.5, width: 0, speed: 0 }
}

function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t
}

/** Window width at resistance `r`: `lerp(windowWidth[0], windowWidth[1], r)` (30% → 12%). */
export function windowWidth(r: number, cfg: Pick<MiningConfig, 'fracture'>): number {
  return lerp(cfg.fracture.windowWidth[0], cfg.fracture.windowWidth[1], r)
}

/** Peak speed of the window centre at resistance `r`: `lerp(windowSpeed[0], windowSpeed[1], r)` (3 → 12 %/s). */
export function windowPeakSpeed(r: number, cfg: Pick<MiningConfig, 'fracture'>): number {
  return lerp(cfg.fracture.windowSpeed[0], cfg.fracture.windowSpeed[1], r)
}

/** Amplitude of the centre's swing: the centre stays in [w/2 + 0.05, 1 − w/2]. */
function windowAmp(w: number): number {
  return (1 - w / 2 - (w / 2 + 0.05)) / 2
}

/** Angular rate of `windowPhase` so the centre's peak speed is `windowPeakSpeed` (0 when the window cannot move). */
function windowOmega(r: number, cfg: Pick<MiningConfig, 'fracture'>): number {
  const amp = windowAmp(windowWidth(r, cfg))
  return amp > 0 ? windowPeakSpeed(r, cfg) / amp : 0
}

/** The window of `state` at resistance `r`: centre `mid + amp × sin(phase)` inside [w/2 + 0.05, 1 − w/2]. */
export function windowAt(state: FractureState, r: number, cfg: Pick<MiningConfig, 'fracture'>, out: FractureWindow): FractureWindow {
  const w = windowWidth(r, cfg)
  const amp = windowAmp(w)
  const mid = (w / 2 + 0.05 + (1 - w / 2)) / 2
  out.width = w
  out.center = mid + amp * Math.sin(state.windowPhase)
  out.speed = amp * windowOmega(r, cfg) * Math.cos(state.windowPhase)
  return out
}

const scratchWindow = createFractureWindow()

/**
 * Advances the fracture on the engaged rock by `dt` at laser `power` (0..1, already smoothed and capped by the
 * fitted laser) and fills `out` (the readout's `fracture` block). `tier` is the rock's resistance tier and
 * `maxTier` the fitted laser's. Returns `start` on the first step of an engagement, `success` / `burst` once
 * when the machine ends (further steps do nothing until `resetFracture`), else `none`.
 */
export function stepFracture(
  state: FractureState,
  power: number,
  dt: number,
  resistance: number,
  maxTier: number,
  tier: number,
  cfg: Pick<MiningConfig, 'fracture'>,
  out: FractureReadout,
): FractureEvent {
  const f = cfg.fracture
  let event: FractureEvent = FRACTURE_EVENT.none
  const tooHard = tier > maxTier
  if (state.phase === FRACTURE_PHASE.idle) {
    state.phase = FRACTURE_PHASE.active
    event = FRACTURE_EVENT.start
  }
  if (state.phase === FRACTURE_PHASE.active) {
    const fill = 1 / f.fractureSeconds
    const win = windowAt(state, resistance, cfg, scratchWindow)
    const lo = win.center - win.width / 2
    const hi = win.center + win.width / 2
    if (tooHard) {
      // "RESISTANCE TOO HIGH": the laser cannot crack this tier; nothing moves.
    } else if (state.cooldown > 0 || power < lo) {
      // Overheated (beam forced off) or under-powered: progress decays, the rock settles.
      state.progress -= f.belowDecay * fill * dt
      state.instability -= f.instabilityDecay * dt
    } else if (power > hi) {
      state.instability += f.instabilityPerOvershoot * (power - hi) * dt
    } else {
      state.progress += fill * dt
      state.instability -= f.instabilityDecay * dt
    }
    if (state.progress < 0) state.progress = 0
    if (state.instability < 0) state.instability = 0
    if (state.instability >= 1) {
      state.instability = 1
      state.phase = FRACTURE_PHASE.burst
      event = FRACTURE_EVENT.burst
    } else if (state.progress >= 1) {
      state.progress = 1
      state.phase = FRACTURE_PHASE.success
      event = FRACTURE_EVENT.success
    }
    state.windowPhase += windowOmega(resistance, cfg) * dt
    if (state.windowPhase > 2 * Math.PI) state.windowPhase -= 2 * Math.PI
  }
  const now = windowAt(state, resistance, cfg, scratchWindow)
  out.active = state.phase === FRACTURE_PHASE.active
  out.progress = state.progress
  out.instability = state.instability
  out.windowCenter = now.center
  out.windowWidth = now.width
  out.tier = tier
  out.tooHard = tooHard
  return event
}
